import { useCallback } from "react";
import { parseError } from "lib";
import { useTranslation } from "locales";
import { useToast } from "@venuepos/react-common";
import { ApolloError } from "@apollo/client";

export function useHandleMutationError() {
    const [t] = useTranslation();
    const toast = useToast();

    const handleMutationError = useCallback(
        async (
            mutatorFunction: () => any,
            successMessage?: string,
            successCallback?: (args?: any) => void,
            errorMessage?: string,
            errorCallback?: (args?: unknown) => void
        ) => {
            try {
                const result = await mutatorFunction();
                if (successMessage) {
                    toast.success(successMessage);
                }
                if (successCallback && typeof successCallback === "function") {
                    successCallback(result);
                }
            } catch (mutatorError) {
                if (errorMessage) {
                    toast.error(errorMessage);
                }

                if (errorCallback && typeof errorCallback === "function") {
                    errorCallback(mutatorError);
                } else {
                    let response: string = "";
                    const parsedError = parseError(mutatorError as ApolloError);

                    if (!parsedError) {
                        console.error(mutatorError);

                        toast.error(JSON.stringify(mutatorError, null, 2), {
                            duration: undefined,
                            showClose: true,
                            title: "An unknown error occurred",
                        });
                        return;
                    }

                    switch (parsedError.type) {
                        case "VALIDATION":
                            response = t(
                                "backoffice.error.validation",
                                "An error occurred: Validation failed."
                            );
                            break;
                        case "NOT_UNIQUE":
                            response = t(
                                "backoffice.error.not_unique",
                                "An error occurred: These data are not unique: {{fields}}",
                                {
                                    fields: parsedError.data.join(", "),
                                }
                            );
                            break;
                        case "IN_USE":
                            response = t(
                                "backoffice.error.in_use",
                                "An error occurred: Item is in use."
                            );
                            break;
                        case "NOT_FOUND":
                            response = t(
                                "backoffice.error.not_found",
                                "An error occurred: Item is not found."
                            );
                            break;
                        case "INVALID_ARGUMENTS":
                            response = t(
                                "backoffice.error.remote_server_error",
                                "An error occurred: {{ message }}",
                                {
                                    message: parsedError.data,
                                    interpolation: { escapeValue: false },
                                }
                            );
                            break;
                        default:
                            response = t(
                                "backoffice.error.unknown",
                                "An unknown error occurred. Please contact the system administrator."
                            );
                    }

                    toast.error(response, {
                        duration: undefined,
                        showClose: true,
                        title: (mutatorError as any)?.type,
                    });
                }
            }
        },
        [t, toast]
    );

    return { handleMutationError };
}
